import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { toast } from "sonner";

import { useNavigate, useSearch } from "@tanstack/react-router";
import { useDeleteBranch, useBranchesQuery } from "../api/queries";
import { BranchesFilter } from "../ui/BranchesFilter";
import { BranchesTable } from "../ui/BranchesTable";
import { BranchesToolbar } from "../ui/BranchesToolbar";

export function BranchesPage() {
  const nav = useNavigate({ from: "/app/branches" });
  const search: OffsetPageQueryDTO = useSearch({ from: "/app/branches" });

  const list = useBranchesQuery({
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
  });

  const canManage = useActionPermission(["branches:create"]);
  const deleteBranch = useDeleteBranch();

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            {
              label: "ສາຂາ",
              to: "/app/branches",
            },
          ]}
        />
      </Header>
      <Main>
        <BranchesToolbar
          canManage={!!canManage}
          onCreate={() => nav({ to: "/app/branches/create" })}
        />

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <BranchesFilter />

          <BranchesTable
            data={list.data?.data ?? []}
            isLoading={list.isLoading}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={list.data?.meta?.total ?? 0}
            onPaginationChange={(offset, limit) =>
              nav({ search: { ...search, offset, limit } })
            }
            sortBy={search.sort ? search.sort[0]?.field : undefined}
            sortOrder={
              search.sort ? (search.sort[0]?.dir as "asc" | "desc") : undefined
            }
            onSortingChange={(id, desc) =>
              nav({
                search: {
                  ...search,
                  sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                },
              })
            }
            onEdit={(row) =>
              nav({
                to: "/app/branches/$id/edit",
                params: { id: row.id },
              })
            }
            onDelete={async (id) =>
              toast.promise(deleteBranch.run(id), {
                loading: "ກຳລັງລຶບ...",
                success: "ລຶບສາຂາສຳເລັດ",
                error: "ລຶບສາຂາລົ້ມເຫຼວ",
              })
            }
            onViewMap={(row) =>
              nav({
                to: "/app/branches/map",
                search: { branch: row.id },
              })
            }
            onSchedule={(row) =>
              nav({
                to: "/app/branches/$id/schedule",
                params: { id: row.id },
              })
            }
          />
        </div>
      </Main>
    </>
  );
}
